/* eslint-disable react/jsx-no-bind */
import React from 'react';

import type { ExtensionHandler } from '@atlaskit/editor-common/extensions';
import { RendererActionsContext } from '@atlaskit/renderer/actions';

import type {
	ContentUnifiedQueryMacroRenderedOutputType,
	ContentUnifiedQueryContentNodeType,
} from '@confluence/content-unified-query';
import {
	EXTENSION_TYPE,
	EXTENSION_KEY,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { TeamCalendarMacroLoader } from '@confluence/team-calendar/entry-points/TeamCalendarMacroLoader.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { RENDERER } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { type ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { UserProfileMacroHandler } from '@confluence/profile-macros/entry-points/UserProfileMacroRenderer.ts';
import { ProfilePictureMacroHandler } from '@confluence/profile-macros/entry-points/ProfilePictureMacroRenderer.ts';
import { ContributorsMacroHandler } from '@confluence/contributors-macro/entry-points/ContributorsMacroRenderer.ts';
import { ExcerptIncludeSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ExcerptIncludeSSRExtensionHandler.ts';
import { AnchorExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/AnchorExtensionHandler.ts';
import { ExpandExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ExpandExtensionHandler.ts';
import { ExcerptExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ExcerptExtensionHandler.ts';
import { DetailsExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/DetailsExtensionHandler.ts';
import { DetailsSummaryExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/DetailsSummaryExtensionHandler.ts';
import { LoadablePlaceholderServer } from '@confluence/loadable/entry-points/LoadablePlaceholder.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';

import { LiveSearchHandler } from './LiveSearchHandler.tsx';
import { TableOfContentsHandler } from './TableOfContentsHandler.tsx';
import { CreateFromTemplateHandler } from './CreateFromTemplateHandler.tsx';
// eslint-disable-next-line import/no-cycle
import { FabricLegacyMacrosServerRenderer } from './FabricLegacyMacrosServerRenderer.tsx';
import { getSsrMigrationExtHandlerForRenderer } from './getSsrMigrationExtHandlerForRenderer.tsx';
import { PlaceholderAwareInlineNodeRendererWrapper } from './PlaceholderAwareInlineNodeRendererWrapper.tsx';

type Options = {
	adf?: string | null;
	spaceKey?: string | null;
	contentId: string;
	cloudId: string;
	userId: string | null;
	includeSuperbatch?: () => void;
	macroRenderedOutput?:
		| (ContentUnifiedQueryMacroRenderedOutputType | ContentUnifiedQueryContentNodeType | null)[]
		| null;
	mediaToken?: string;
};

export const getSsrExtensionHandlers = ({
	adf,
	contentId,
	spaceKey,
	cloudId,
	userId,
	macroRenderedOutput,
	includeSuperbatch,
	mediaToken,
}: Options): NonNullable<ADFRendererProps['extensionHandlers']> => ({
	[EXTENSION_TYPE.MACRO_CORE]: getMacroExtensionHandlerForRenderer({
		adf,
		contentId,
		spaceKey,
		cloudId,
		userId,
		macroRenderedOutput,
		includeSuperbatch,
		mediaToken,
	}),
	[EXTENSION_TYPE.MACRO_WITH_REFERENCES]: getEmptyHandlers(),
	[EXTENSION_TYPE.MIGRATION]: getSsrMigrationExtHandlerForRenderer({
		contentId,
	}),
	[EXTENSION_TYPE.TEMPLATE]: getEmptyHandlers(),
});

const getEmptyHandlers = (): ExtensionHandler<any> => createMacroExtensionHandler(() => null);

export const getExtensionHandlersMap = ({
	macroDefaultProps,
	extension,
	cloudId,
	contentId,
	userId,
	adf,
	mediaToken = undefined,
}) => {
	function getSsrButtonLabel() {
		const { buttonLabel, createButtonLabel } = macroDefaultProps.node.parameters.macroParams;

		const labelText = buttonLabel?.value || createButtonLabel?.value;
		return labelText || undefined;
	}

	return {
		[EXTENSION_KEY.TOC]: () => (
			<TableOfContentsHandler
				macroDefaultProps={macroDefaultProps}
				adf={adf}
				cloudId={cloudId}
				userId={userId}
			/>
		),
		[EXTENSION_KEY.CREATE_FROM_TEMPLATE]: () => (
			<CreateFromTemplateHandler
				macroDefaultProps={macroDefaultProps}
				cloudId={cloudId}
				userId={userId}
				ssrButtonLabel={getSsrButtonLabel()}
			/>
		),
		[EXTENSION_KEY.LIVE_SEARCH]: () => <LiveSearchHandler macroDefaultProps={macroDefaultProps} />,
		[EXTENSION_KEY.DETAILS]: () => (
			<DetailsExtensionHandler
				{...macroDefaultProps}
				Renderer={ADFRenderer}
				isLegacyRenderer={false}
				getExtensionHandlers={getSsrExtensionHandlers}
			/>
		),
		[EXTENSION_KEY.EXPAND]: () => (
			<ExpandExtensionHandler
				{...macroDefaultProps}
				Renderer={ADFRenderer}
				isLegacyRenderer={false}
				getExtensionHandlers={getSsrExtensionHandlers}
			/>
		),
		[EXTENSION_KEY.EXCERPT]: () => (
			<ExcerptExtensionHandler
				{...macroDefaultProps}
				Renderer={ADFRenderer}
				isLegacyRenderer={false}
				getExtensionHandlers={getSsrExtensionHandlers}
				mediaToken={mediaToken}
			/>
		),
		[EXTENSION_KEY.ANCHOR]: () => <AnchorExtensionHandler {...macroDefaultProps} isSSR />,
		[EXTENSION_KEY.EXCERPT_INCLUDE]: () => {
			if (contentId) {
				return (
					<ExcerptIncludeSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={getSsrExtensionHandlers}
					/>
				);
			} else {
				return null;
			}
		},
		[EXTENSION_KEY.PROFILE_PICTURE]: () => (
			<ProfilePictureMacroHandler
				macroDefaultProps={macroDefaultProps}
				macroParams={extension?.parameters?.macroParams}
			/>
		),
		[EXTENSION_KEY.PROFILE]: () => (
			<UserProfileMacroHandler
				macroDefaultProps={macroDefaultProps}
				macroParams={extension?.parameters?.macroParams}
			/>
		),
		[EXTENSION_KEY.CONTRIBUTORS]: () => {
			if (expValEquals('cc_rewrite_contributors_legacy_macro', 'isEnabled', true)) {
				return (
					<ContributorsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.DETAILS_SUMMARY]: () => {
			if (expValEquals('confluence_ttvc_macro_detailssummary', 'isEnabled', true)) {
				return (
					<DetailsSummaryExtensionHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.TEAM_CALENDARS]: () => {
			if (expValEquals('cc-engagement-team-calendar-macro-afm-migration', 'isEnabled', true)) {
				return (
					<TeamCalendarMacroLoader
						extensionNode={extension}
						macroDefaultProps={macroDefaultProps}
					/>
				);
			}
		},
		// the hide macro should render nothing in renderer
		...(expVal('platform_editor_hide_extension_renderer_support', 'isEnabled', false)
			? { [EXTENSION_KEY.HIDE]: () => <div data-extension-key="hide" /> }
			: {}),
		/* These macros have React implementation on SPA */
		[EXTENSION_KEY.REDACTED_MACRO_PLACEHOLDER]: () => null,
		[EXTENSION_KEY.CONFLUENCE_DATABASES]: () => null,
		// Native-embed extensions use dynamic keys (e.g. 'native-embed:whiteboard',
		// 'native-embed:db'). When the current extension is a native-embed, add an
		// SSR placeholder entry so the map lookup succeeds and we avoid the legacy
		// macro renderer (which would show "Unknown macro").
		...(extension?.extensionKey?.startsWith(EXTENSION_KEY.NATIVE_EMBED)
			? { [extension.extensionKey]: () => <div data-testid="native-embed-ssr-placeholder" /> }
			: {}),
	};
};

const getMacroExtensionHandlerForRenderer = ({
	adf,
	contentId,
	spaceKey,
	cloudId,
	userId,
	includeSuperbatch,
	macroRenderedOutput,
	mediaToken,
}): ExtensionHandler<any> =>
	createMacroExtensionHandler((extension) => {
		const { type, extensionKey } = extension;
		const macroId = extension?.parameters?.macroMetadata?.macroId?.value;

		const macroDefaultProps: ExtensionHandlerProps = {
			node: extension,
			contentId,
			spaceKey,
			mode: RENDERER,
			extensionKey,
		};

		// This callback won't be available on a recursive call for handlers from bodied macros
		// but it only needs to be called on the top level of recursion.
		if (includeSuperbatch) {
			includeSuperbatch();
		}

		const serverExtensionHandlersMap = getExtensionHandlersMap({
			adf,
			userId,
			contentId,
			cloudId,
			extension,
			macroDefaultProps,
			...(expVal('confluence_frontend_fix_excerpt_media_issue', 'isEnabled', false)
				? { mediaToken }
				: {}),
		});

		const handlerContent = serverExtensionHandlersMap[extensionKey];

		// When early webpanel initialization is enabled, Connect resources are preloaded during SSR.
		// To prevent flickering during React hydration, we need to hide Connect macros in the SSR phase
		// by setting renderedByProvider=false. After hydration, when macros are re-rendered through
		// the provider flow (LegacyMacroRendererComponent), they will have renderedByProvider=true
		const shouldHideDuringSSR =
			fg('content_early_connect_iframe_initialization') ||
			expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false);

		const content =
			typeof handlerContent === 'function' ? (
				handlerContent()
			) : (
				<FabricLegacyMacrosServerRenderer
					macroRenderedOutput={macroRenderedOutput}
					macroDefaultProps={macroDefaultProps}
					macroOutput={extension?.parameters?.macroOutput}
					renderedByProvider={!shouldHideDuringSSR}
				/>
			);

		// This boundary is not directly used but it provides reference for fixing nested div under span.
		// See next/packages/ssr-scripts-utils/src/fixUnbalancedPlaceholderCommentMarkers.ts for details
		return (
			<RendererActionsContext>
				<LoadablePlaceholderServer placeholderId={`legacy-macro-container-${macroId}`}>
					{
						// Mimic the logic in platform/packages/editor/renderer/src/ui/ExtensionRenderer.tsx
						// We don't update the platform code for 2 reasons:
						// 1. Backwards compatibility. I don't know if other product supplies this wrapper already (unlikely)
						// 2. More importantly this needs to be inside the legacy-macro-container-${macroId} boundary.
						//    Inline macros has <span> nested in <div> problem. We are using the boundary as guide to fix them.
						//    See next/packages/ssr-scripts-utils/src/fixUnbalancedPlaceholderCommentMarkers.ts
						type === 'inlineExtension' ? (
							<PlaceholderAwareInlineNodeRendererWrapper macroId={macroId}>
								{content}
							</PlaceholderAwareInlineNodeRendererWrapper>
						) : (
							content
						)
					}
				</LoadablePlaceholderServer>
			</RendererActionsContext>
		);
	});
